import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Image as ImageIcon, Upload, X, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ROUTES } from "@/config/constants";
import type { ProofType } from "@/types";
import { useLocalization } from "@/hooks/useLocalization";
import { RichTextEditor } from "@/components/RichTextEditor";

const EditBounty = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const { formatCurrency } = useLocalization();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [proofType, setProofType] = useState<ProofType>("photo");
  const [rewardCents, setRewardCents] = useState(200);
  const [referencePhotos, setReferencePhotos] = useState<string[]>([]);
  const [newPhotoFiles, setNewPhotoFiles] = useState<File[]>([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  
  useEffect(() => {
    if (!id || !user) return;
    
    const loadBounty = async () => {
      const { data, error } = await supabase
        .from("bounties")
        .select("*")
        .eq("id", id)
        .single();
      
      if (error) {
        toast.error("Failed to load bounty");
        nav(ROUTES.HOME);
        return;
      }
      
      // Check if user is the owner
      if (data.requester_id !== user.id) {
        toast.error("You can only edit your own bounties");
        nav(ROUTES.HOME);
        return;
      }
      
      setTitle(data.title);
      setDescription(data.description);
      setLocation(data.location || "");
      setProofType(data.proof_type);
      setRewardCents(data.reward_cents);
      setReferencePhotos(data.reference_photos || []);
      setLoading(false);
    };
    
    loadBounty();
  }, [id, user, nav]);
  
  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    
    if (!description.trim()) {
      toast.error("Description is required");
      return;
    }
    
    setSaving(true);
    
    try {
      // Upload new photos first
      let finalPhotos = [...referencePhotos];
      
      if (newPhotoFiles.length > 0) {
        setUploadingPhotos(true);
        for (const photo of newPhotoFiles) {
          const fileName = `${user!.id}/${id}/${Date.now()}_${photo.name}`;
          
          const { error: uploadError } = await supabase.storage
            .from('bounty-reference-photos')
            .upload(fileName, photo);
          
          if (uploadError) throw uploadError;
          
          const { data: { publicUrl } } = supabase.storage
            .from('bounty-reference-photos')
            .getPublicUrl(fileName);
          
          finalPhotos.push(publicUrl);
        }
        setUploadingPhotos(false);
      }
      
      // Update bounty with all data including photos. Funding fields
      // (reward_cents, max_claimers, duration, status) are server-protected
      // and cannot be changed after publication.
      const { error } = await supabase
        .from("bounties")
        .update({
          title: title.trim(),
          description: description.trim(),
          location: location.trim() || null,
          proof_type: proofType,
          reference_photos: finalPhotos,
        })
        .eq("id", id)
        .eq("requester_id", user!.id);
      
      if (error) throw error;
      
      toast.success("Bounty updated successfully");
      nav(ROUTES.BOUNTY_DETAIL(id!));
    } catch (error: any) {
      toast.error(error.message || "Failed to update bounty");
    } finally {
      setSaving(false);
      setUploadingPhotos(false);
    }
  };
  
  const handleCancel = () => {
    nav(ROUTES.BOUNTY_DETAIL(id!));
  };
  
  const handleAddPhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const newFiles = Array.from(files);
    setNewPhotoFiles(prev => [...prev, ...newFiles]);
  };
  
  const handleRemoveNewPhoto = (index: number) => {
    setNewPhotoFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleRemoveExistingPhoto = (index: number) => {
    setReferencePhotos(prev => prev.filter((_, i) => i !== index));
  };
  
  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="container max-w-2xl py-6 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen">
      <Header />
      <main className="container max-w-2xl py-6">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={handleCancel}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Edit Bounty</h1>
        </div>
        
        <div className="poster p-4 pt-8 space-y-5">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="font-bold">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What do you need done?"
              className="border-2 border-[hsl(var(--paper-edge))] focus:border-[hsl(45_85%_55%)] transition-colors"
            />
          </div>
          
          {/* Description */}
          <div className="space-y-2">
            <Label className="font-bold">Description</Label>
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder="Describe the task in detail..."
            />
          </div>
          
          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location" className="font-bold">Location (Optional)</Label>
            <Input
              id="location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Where should this be done?"
              className="border-2 border-[hsl(var(--paper-edge))] focus:border-[hsl(45_85%_55%)] transition-colors"
            />
          </div>
          
          {/* Proof Type */}
          <div className="space-y-2">
            <Label className="font-bold">Proof Type</Label>
            <Select value={proofType} onValueChange={(val) => setProofType(val as ProofType)}>
              <SelectTrigger className="border-2 border-[hsl(var(--paper-edge))]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="photo">Photo</SelectItem>
                <SelectItem value="screenshot">Screenshot</SelectItem>
                <SelectItem value="link">Link</SelectItem>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="video">Video</SelectItem>
                <SelectItem value="document">PDF/Document</SelectItem>
                <SelectItem value="audio">Audio</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Reward - locked after publication; changes require a new funded listing */}
          <div className="space-y-2">
            <Label className="font-bold">Reward Amount</Label>
            <div className="flex items-center gap-2">
              <div className="flex-1 rounded-md border-2 border-[hsl(var(--paper-edge))] bg-muted px-3 py-2 text-sm font-semibold">
                {formatCurrency(rewardCents)}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Reward changes require a new funded listing.
            </p>
          </div>
          
          {/* Reference Photos */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="font-bold flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                Reference Photos ({referencePhotos.length + newPhotoFiles.length})
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('edit-photo-upload')?.click()}
                disabled={uploadingPhotos}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Photos
              </Button>
              <Input
                id="edit-photo-upload"
                type="file"
                accept="image/*"
                multiple
                onChange={handleAddPhotos}
                className="hidden"
              />
            </div>
            
            {/* Existing Photos */}
            {referencePhotos.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Current Photos:</p>
                <div className="grid grid-cols-3 gap-3">
                  {referencePhotos.map((url, index) => (
                    <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border-2 border-[hsl(var(--paper-edge))]">
                      <img src={url} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => handleRemoveExistingPhoto(index)}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* New Photos to Upload */}
            {newPhotoFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">New Photos to Upload:</p>
                <div className="grid grid-cols-3 gap-3">
                  {newPhotoFiles.map((file, index) => (
                    <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border-2 border-primary bg-primary/5">
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center p-2">
                          <Upload className="h-6 w-6 mx-auto text-primary mb-1" />
                          <p className="text-xs text-primary truncate">{file.name}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveNewPhoto(index)}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {referencePhotos.length === 0 && newPhotoFiles.length === 0 && (
              <div className="p-6 border-2 border-dashed border-[hsl(var(--paper-edge))] rounded-lg text-center">
                <ImageIcon className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No reference photos yet</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => document.getElementById('edit-photo-upload')?.click()}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Upload Photos
                </Button>
              </div>
            )}
          </div>
          
          {/* Warning */}
          <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border-2 border-amber-300 dark:border-amber-700 rounded-lg">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Note:</strong> You cannot change the bounty after it has been claimed by a hunter.
            </p>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t-2 border-[hsl(var(--paper-edge))]">
            <Button variant="outline" className="flex-1" onClick={handleCancel}>
              Cancel
            </Button>
            <Button 
              className="flex-1 bg-gradient-to-r from-[hsl(45_85%_55%)] to-[hsl(35_75%_42%)] hover:from-[hsl(45_80%_50%)] hover:to-[hsl(35_70%_38%)] text-[hsl(25_50%_12%)]"
              onClick={handleSave}
              disabled={saving || uploadingPhotos}
            >
              {(saving || uploadingPhotos) ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EditBounty;
